import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, X, Upload, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    location_area: '',
    location_city: '',
    location_map_url: '',
    status: 'upcoming',
    apartment_types: [],
    expected_launch: '',
    construction_status: '',
    possession_timeline: '',
    year_completed: '',
    description: '',
    amenities: [],
    images: [],
    floor_plans: [],
    highlights: '',
    site_images_title: 'Actual Site',
    site_images: [],
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        ...project,
        location_map_url: project.location_map_url || '',
        apartment_types: project.apartment_types || [],
        amenities: project.amenities || [],
        images: project.images || [],
        floor_plans: project.floor_plans || [],
        site_images_title: project.site_images_title || 'Actual Site',
        site_images: project.site_images || [],
      });
    } else {
      setEditingProject(null);
      setFormData({
        name: '',
        location_area: '',
        location_city: '',
        location_map_url: '',
        status: 'upcoming',
        apartment_types: [],
        expected_launch: '',
        construction_status: '',
        possession_timeline: '',
        year_completed: '',
        description: '',
        amenities: [],
        images: [],
        floor_plans: [],
        highlights: '',
        site_images_title: 'Actual Site',
        site_images: [],
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    // Normalize array fields: trim each entry and drop empty entries before sending
    const arrayFields = ['apartment_types', 'amenities', 'images', 'floor_plans'];
    const payload = { ...formData };
    arrayFields.forEach((f) => {
      if (Array.isArray(payload[f])) {
        payload[f] = payload[f].map((v) => (typeof v === 'string' ? v.trim() : v)).filter(Boolean);
      }
    });

    try {
      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Project updated successfully');
      } else {
        await axios.post(`${API}/projects`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Project created successfully');
      }
      setShowModal(false);
      fetchProjects();
    } catch (error) {
      console.error('Error saving project:', error);
      toast.error('Failed to save project');
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Project deleted successfully');
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    }
  };

  const handleArrayInput = (field, value) => {
    // Preserve raw input while typing (do not trim/filter here);
    // the submit handler normalizes array fields before sending to backend.
    const items = value.split(',');
    setFormData({ ...formData, [field]: items });
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    const token = localStorage.getItem('admin_token');
    const uploadedUrls = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const response = await axios.post(`${API}/upload`, formDataUpload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });

        uploadedUrls.push(response.data.url);
      }

      // Add uploaded images to existing images
      setFormData({
        ...formData,
        images: [...formData.images, ...uploadedUrls],
      });

      toast.success(`${uploadedUrls.length} image(s) uploaded successfully`);
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Failed to upload images');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleFloorPlanUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    const token = localStorage.getItem('admin_token');
    const uploadedUrls = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const response = await axios.post(`${API}/upload`, formDataUpload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });

        uploadedUrls.push(response.data.url);
      }

      // Add uploaded floor plans to existing floor plans
      setFormData({
        ...formData,
        floor_plans: [...formData.floor_plans, ...uploadedUrls],
      });

      toast.success(`${uploadedUrls.length} floor plan(s) uploaded successfully`);
    } catch (error) {
      console.error('Error uploading floor plans:', error);
      toast.error('Failed to upload floor plans');
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (index) => {
    const newImages = [...formData.images];
    newImages.splice(index, 1);
    setFormData({ ...formData, images: newImages });
  };

  const removeFloorPlan = (index) => {
    const newFloorPlans = [...formData.floor_plans];
    newFloorPlans.splice(index, 1);
    setFormData({ ...formData, floor_plans: newFloorPlans });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/admin/dashboard" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_34f43037-f49f-4896-a120-3461406a9d35/artifacts/wh0yojmq_e30d7691-c116-46a6-b645-fa81d1964b10-removebg-preview.png" 
                alt="Infinity Group" 
                className="h-20 w-auto"
              />
              <span className="text-xl font-bold text-gray-900">Infinity Group</span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900" data-testid="admin-projects-heading">
            Manage Projects
          </h1>
          <Button
            onClick={() => handleOpenModal()}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="add-project-btn"
          >
            <Plus size={18} className="mr-2" />
            Add Project
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {projects.map((project) => (
                    <tr key={project.id} data-testid={`project-row-${project.id}`}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{project.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {project.location_area}, {project.location_city}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`status-badge status-${project.status}`}>
                          {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleOpenModal(project)}
                            className="text-blue-600 hover:text-blue-800"
                            data-testid={`edit-project-${project.id}`}
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(project.id)}
                            className="text-red-600 hover:text-red-800"
                            data-testid={`delete-project-${project.id}`}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProject ? 'Edit Project' : 'Add New Project'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="project-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="input-project-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="select-project-status"
                >
                  <option value="upcoming">Upcoming</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location Area *</label>
                <input
                  type="text"
                  value={formData.location_area}
                  onChange={(e) => setFormData({ ...formData, location_area: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="input-location-area"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location City *</label>
                <input
                  type="text"
                  value={formData.location_city}
                  onChange={(e) => setFormData({ ...formData, location_city: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="input-location-city"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Google Maps URL <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="url"
                value={formData.location_map_url || ''}
                onChange={(e) => setFormData({ ...formData, location_map_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="https://maps.app.goo.gl/... or https://www.google.com/maps/place/..."
                data-testid="input-location-map-url"
              />
              <p className="text-xs text-gray-500 mt-1">
                Open Google Maps, search the project location, click <strong>Share</strong> → <strong>Copy link</strong>, paste here.
                If empty, clicking the project address will search Google Maps using "Area, City".
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Apartment Types * (comma-separated: e.g., 2 BHK, 3 BHK)
              </label>
              <input
                type="text"
                value={formData.apartment_types.join(',')}
                onChange={(e) => handleArrayInput('apartment_types', e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="input-apartment-types"
              />
            </div>

            {formData.status === 'upcoming' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expected Launch</label>
                <input
                  type="text"
                  value={formData.expected_launch}
                  onChange={(e) => setFormData({ ...formData, expected_launch: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Q2 2025"
                  data-testid="input-expected-launch"
                />
              </div>
            )}

            {formData.status === 'ongoing' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Construction Status</label>
                  <input
                    type="text"
                    value={formData.construction_status}
                    onChange={(e) => setFormData({ ...formData, construction_status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., 60% Complete"
                    data-testid="input-construction-status"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Possession Timeline</label>
                  <input
                    type="text"
                    value={formData.possession_timeline}
                    onChange={(e) => setFormData({ ...formData, possession_timeline: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., Dec 2025"
                    data-testid="input-possession-timeline"
                  />
                </div>
              </>
            )}

            {formData.status === 'completed' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year Completed</label>
                <input
                  type="text"
                  value={formData.year_completed}
                  onChange={(e) => setFormData({ ...formData, year_completed: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., 2023"
                  data-testid="input-year-completed"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="input-description"
              ></textarea>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amenities (comma-separated)
              </label>
              <input
                type="text"
                value={formData.amenities.join(',')}
                onChange={(e) => handleArrayInput('amenities', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="e.g., Swimming Pool, Gym, Kids Play Area"
                data-testid="input-amenities"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Project Images
              </label>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <label className="flex-1 cursor-pointer">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition-colors">
                      <Upload size={24} className="mx-auto mb-2 text-gray-400" />
                      <p className="text-sm text-gray-600">Click to upload images</p>
                      <p className="text-xs text-gray-400 mt-1">JPG, PNG (Max 5MB each)</p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploadingImages}
                    />
                  </label>
                </div>
                {uploadingImages && (
                  <p className="text-sm text-blue-600">Uploading images...</p>
                )}
                {formData.images.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">Uploaded Images:</p>
                    {formData.images.map((img, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                        <img src={img} alt={`Preview ${idx + 1}`} className="w-16 h-16 object-cover rounded" />
                        <span className="flex-1 text-xs text-gray-600 truncate">Image {idx + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="text-xs text-gray-500">
                  Or paste image URLs below (comma-separated)
                </div>
                <textarea
                  value={formData.images.join(',')}
                  onChange={(e) => handleArrayInput('images', e.target.value)}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg"
                  data-testid="input-images"
                ></textarea>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Floor Plans
              </label>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <label className="flex-1 cursor-pointer">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition-colors">
                      <Upload size={24} className="mx-auto mb-2 text-gray-400" />
                      <p className="text-sm text-gray-600">Click to upload floor plans</p>
                      <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG (Max 10MB each)</p>
                    </div>
                    <input
                      type="file"
                      accept=".pdf,image/*"
                      multiple
                      onChange={handleFloorPlanUpload}
                      className="hidden"
                      disabled={uploadingImages}
                    />
                  </label>
                </div>
                {formData.floor_plans.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">Uploaded Floor Plans:</p>
                    {formData.floor_plans.map((plan, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                        <FileText size={16} className="text-blue-600" />
                        <span className="flex-1 text-xs text-gray-600 truncate">Floor Plan {idx + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeFloorPlan(idx)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="text-xs text-gray-500">
                  Or paste floor plan URLs below (comma-separated)
                </div>
                <textarea
                  value={formData.floor_plans.join(',')}
                  onChange={(e) => handleArrayInput('floor_plans', e.target.value)}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="https://example.com/floorplan.pdf"
                ></textarea>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Highlights</label>
              <textarea
                value={formData.highlights}
                onChange={(e) => setFormData({ ...formData, highlights: e.target.value })}
                rows="2"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="input-highlights"
              ></textarea>
            </div>

            {/* Site Images Section */}
            <div className="border-t pt-4 mt-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Site Progress Images</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                  <input
                    type="text"
                    value={formData.site_images_title}
                    onChange={(e) => setFormData({ ...formData, site_images_title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., Actual Site, Construction Progress"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Site Images</label>
                  <div className="flex items-center gap-3 mb-3">
                    <label className="flex-1 cursor-pointer">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-green-500 transition-colors">
                        <Upload size={24} className="mx-auto mb-2 text-gray-400" />
                        <p className="text-sm text-gray-600">Click to upload site images</p>
                        <p className="text-xs text-gray-400 mt-1">JPG, PNG (Max 5MB each)</p>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={async (e) => {
                          const files = e.target.files;
                          if (!files || files.length === 0) return;
                          
                          const token = localStorage.getItem('admin_token');
                          const newSiteImages = [];
                          
                          for (let i = 0; i < files.length; i++) {
                            const file = files[i];
                            const formDataUpload = new FormData();
                            formDataUpload.append('file', file);
                            
                            try {
                              const response = await axios.post(`${API}/upload`, formDataUpload, {
                                headers: {
                                  Authorization: `Bearer ${token}`,
                                  'Content-Type': 'multipart/form-data',
                                },
                              });
                              newSiteImages.push({
                                url: response.data.url,
                                caption: '',
                                date: new Date().toLocaleDateString('en-IN'),
                              });
                            } catch (error) {
                              console.error('Error uploading:', error);
                            }
                          }
                          
                          setFormData({
                            ...formData,
                            site_images: [...formData.site_images, ...newSiteImages],
                          });
                          toast.success(`${newSiteImages.length} site image(s) uploaded`);
                        }}
                        className="hidden"
                      />
                    </label>
                  </div>
                  
                  {formData.site_images && formData.site_images.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-gray-700">Uploaded Site Images:</p>
                      {formData.site_images.map((siteImg, idx) => (
                        <div key={idx} className="flex items-start gap-3 bg-gray-50 p-3 rounded-lg">
                          <img src={siteImg.url} alt={`Site ${idx + 1}`} className="w-20 h-20 object-cover rounded" />
                          <div className="flex-1 space-y-2">
                            <input
                              type="text"
                              value={siteImg.caption || ''}
                              onChange={(e) => {
                                const updated = [...formData.site_images];
                                updated[idx].caption = e.target.value;
                                setFormData({ ...formData, site_images: updated });
                              }}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                              placeholder="Caption (optional)"
                            />
                            <input
                              type="text"
                              value={siteImg.date || ''}
                              onChange={(e) => {
                                const updated = [...formData.site_images];
                                updated[idx].date = e.target.value;
                                setFormData({ ...formData, site_images: updated });
                              }}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                              placeholder="Date (e.g., 15 Mar 2026)"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const updated = formData.site_images.filter((_, i) => i !== idx);
                              setFormData({ ...formData, site_images: updated });
                            }}
                            className="text-red-600 hover:text-red-800 p-1"
                          >
                            <X size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="save-project-btn">
                {editingProject ? 'Update Project' : 'Create Project'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                data-testid="cancel-project-btn"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProjects;